import { observable, action, computed } from 'mobx';
import { Type } from '@/pages/home/component/DiffComponent/DiffEditor';
import {
  AuditInfoProps, DiffInterfaceItem, ConfigVersionTypeEnum, AuditStatusEnum, PluginDiff, PluginDiffSubmitList,
} from '@/pages/home/type/configServerType';
import { formatDiffCode } from '@/pages/home/utils/config';
import { snakeToCamle, getQueryParams } from '@/utils/common';
import { get } from 'lodash';
import {
  getEnvironmentList, getEnvironmentDetail, getInterfaceDiffInfo, getServiceInfo,
} from '../server/configServer';

class ConfigStore {
  // diff页面的类型 submit preview publish
  @observable diffType = 'submit';

  // plat的id
  @observable serviceId = '';

  // 审核版本的id
  @observable auditId = '';

  @observable currentServiceInfo: any = null;

  // 环境id
  @observable environmentId = '';

  @observable auditInfo?: AuditInfoProps;

  @observable auditDescription?: string;

  // 环境版本信息
  @observable versionInfo = [];

  // idldiff信息
  @observable idlDiff: any = {};

  // 自定义插件diff信息
  @observable customPluginDiff: PluginDiff[] = [];

  // 自定义插件diff信息
  @computed get pluginConfigList() {
    const pluginConfigList: PluginDiffSubmitList[] = [];
    this.checkedList.forEach((item) => {
      const pluginConfigItem = this.customPluginDiff.find((ite) => ite.path === item.path);
      if (pluginConfigItem) {
        pluginConfigList.push(...(pluginConfigItem.plugins.map((plugin) => ({
          configId: plugin.newConfig?.configId || plugin.oldConfig?.configId || '',
          type: pluginConfigItem.type,
        })).filter((ite) => Boolean(ite.configId))));
      }
    });
    return pluginConfigList;
  }

  // 修改接口的列表
  @observable interfaceList: DiffInterfaceItem[] = [];

  // 接口列表
  @observable historyListData: DiffInterfaceItem[] = [];

  // 当前操作接口的id
  @observable activeInterfaceId = 0;

  // 获取当前的接口diff的代码
  @observable currentDiffInfo = {
    newCode: '',
    oldCode: '',
    lang: 'yaml',
    type: 'interface',
  };

  // 更新diff列表
  @observable refreshList = false;

  // diff加载中状态
  @observable diffStatus = 'Loading';

  // 当前是否更新IDL
  @observable updateIDL = false;

  // 当前diff的配置类型
  @observable diffConfigType = Type.router;

  // 是否展示提交审核modal
  @observable isShowAuditModal = false;

  // 是否展示上线modal
  @observable isShowPublishModal = false;

  // 当前选中diff的接口
  @observable checkedList: DiffInterfaceItem[] = [];

  // 选择的接口数据
  @observable configList = [];

  // 接口是否可以去编辑
  @observable isCanEdit = false;

  // 灰度发布配置
  @observable grayConfig = {
    releaseId: 0,
    type: 'random',
    policy: {
      percent: 0,
    },
  };

  // 重置Store
  @action
  resetDiffStore() {
    this.serviceId = '';
    this.environmentId = '';
    this.idlDiff = {};
    this.interfaceList = [];
    this.historyListData = [];
    this.activeInterfaceId = 0;
    this.currentDiffInfo = {
      newCode: '',
      oldCode: '',
      lang: 'yaml',
      type: 'interface',
    };
    this.refreshList = false;
    this.updateIDL = false;
    this.diffConfigType = Type.router;
    this.checkedList = [];
    this.auditInfo = undefined;
    this.configList = [];
    this.isCanEdit = false;
    this.currentServiceInfo = null;
  }

  @action
  async getServiceInfo() {
    const teamId = getQueryParams('team_id');
    const result = await getServiceInfo({
      serviceId: this.serviceId,
      teamId,
    });

    this.currentServiceInfo = get(result, 'data.serviceList[0]', null);
  }

  // 获取diff列表
  @action
  async getDiffList(serviceId: string, auditId?: string) {
    this.diffStatus = 'Loading';
    let result = await getInterfaceDiffInfo({ serviceId, auditId });
    result = snakeToCamle(result);
    const configList = get(result, 'data.configList', []);
    this.interfaceList = get(result, 'data.interfaceDiff', []);
    this.historyListData = get(result, 'data.interfaceDiff', []);
    this.idlDiff = get(result, 'data.idlDiff[0]', {});
    this.environmentId = get(result, 'data.environmentId', '');
    this.diffStatus = 'Success';
    this.auditInfo = get(result, 'data.approval', {});
    this.auditDescription = get(result, 'data.description', '');
    this.customPluginDiff = get(result, 'data.customPluginDiff', []) || [];
    if (this.interfaceList.length > 0) {
      this.currentDiffInfo = formatDiffCode(this.interfaceList[this.activeInterfaceId]);
    }
    if (configList && configList.length) {
      this.configList = configList;
      this.checkedList = this.interfaceList;
    }
  }

  // 获取环境列表
  @action
  async getEnvList(serviceId: string) {
    const result = await getEnvironmentList({ serviceId });
    this.versionInfo = get(result, 'data.environmentList', []);
    const auditVersion: any = this.versionInfo.find((item: any) => item.name === ConfigVersionTypeEnum.Audit);
    this.isCanEdit = !auditVersion || [AuditStatusEnum.Rejected, AuditStatusEnum.Expire].includes(auditVersion?.auditStatus!);
  }

  // 获取环境基本信息
  @action
  async getEnvDetail() {
    const result = await getEnvironmentDetail({ serviceId: this.serviceId, environmentId: this.environmentId });
    this.grayConfig = get(result, 'data.config.grayConfig', {
      releaseId: 0,
      type: 'random',
      policy: {
        percent: 0,
      },
    });
  }
}

export default { name: 'diffStore', Store: ConfigStore };
